import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetDoctorsNotes,
  useGetImproveScoreSummary,
  useGetL4ConnectionList,
  useGetScoreDetails,
} from '@viome/viome-base/src/usecase/Health/HealthQuery';
import { HealthDetailsSectionHeader } from '@screens/HealthPages/HealthDetailsSectionHeader';
import { StandardContainer } from '@design-system/StandardContainer';
import { DoctorsNotes } from '@screens/LevelTwoDetail/DoctorsNotes';
import { ConnectionCard } from '@design-system/Cards/ConnectionCard';
import { SkeletonView } from '@screens/HealthConnections/HealthConnectionsContainer';
import { HealthL4ImproveMyScoreInfo } from '@screens/HealthPages/HealthL4ImproveMyScoreInfo';
import { ConditionalRender } from '@/components/Helpers/ComponentRender/ConditionalRender';
import DoctorsNoteShimmer from '@screens/LevelTwoDetail/DoctorsNoteShimmer';
import ImproveMyScoreShimmer from '@screens/LevelTwoDetail/ImproveMyScoreShimmer';
import { EventTracker } from '@/utils';

export const HealthL4DetailsPage = () => {
  const { id: scoreId } = useParams();
  const navigate = useNavigate();

  const {
    data: l4ScoreDetail,
    isLoading: isL4ScoreDetailAPILoading,
    isError: isL4ScoreDetailAPIError,
  } = useGetScoreDetails('L4', Number(scoreId));

  const {
    data: doctorNotes,
    isLoading: isDoctorsNoteLoading,
    isError: isDoctorsNoteError,
  } = useGetDoctorsNotes('L4', Number(scoreId));

  const {
    data: healthConnectionList,
    isLoading: isHealthConnectionAPILoading,
    isError: isHealthConnectionAPIError,
  } = useGetL4ConnectionList(Number(scoreId));

  const {
    data: improveMyScore,
    isLoading: isImproveMyScoreLoading,
    isError: isImproveMyScoreError,
  } = useGetImproveScoreSummary(Number(scoreId));

  const navigateToConditionsPage = (title: string) => {
    return navigate(`/health/connections/${encodeURIComponent(title)}`);
  };

  const renderDoctorsNotWithBg = () => {
    if (!doctorNotes) return;
    return (
      <section
        className="bg-black bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 1) 100%), url('https://viome-cdn.s3.amazonaws.com/media/app/v2/png/home/image_for_doctors_notes.png')`,
        }}
      >
        <StandardContainer>
          <DoctorsNotes notes={doctorNotes} />
        </StandardContainer>
      </section>
    );
  };

  const renderImproveMyScore = () => {
    return (
      <section>
        {improveMyScore && (
          <HealthL4ImproveMyScoreInfo improveMyScoreInfo={improveMyScore} />
        )}
      </section>
    );
  };

  const renderHealthConnections = (title: string) => {
    if (healthConnectionList && healthConnectionList?.length < 1) {
      return;
    }
    // TODO: make it a separate component
    return (
      <div className="mt-12 mb-24">
        <div className="flex flex-col">
          <div className="dark typography-h1 text-primary mb-2 flex">
            Health Connections
          </div>
          <div className="flex dark typography-body text-secondary mb-6 lg:w-[35rem]">
            We’ve found a connection between this pathway score and a condition.
            This condition may be one you self-reported or an Associated Health
            Risk identified through our research.
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {healthConnectionList?.map((connection) => (
            <div key={`${connection.name}-${connection.id}`}>
              <ConnectionCard
                name={connection.name}
                noOfConnections={connection.no_of_connections}
                connectionType={connection.connection_type}
                connectionTypeColor={connection.connection_type_color}
                onClick={() => {
                  EventTracker.trackEvent(
                    'health',
                    `${title.replace(/\s+/g, '')}Details_healthConnections_${connection.name.replace(/\s+/g, '')}_reported`,
                  );
                  navigateToConditionsPage(connection.name);
                }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-black h-full text-primary w-full dark !max-w-full">
      <HealthDetailsSectionHeader
        scoreDetail={l4ScoreDetail}
        isScoreDetailsAPILoading={isL4ScoreDetailAPILoading}
        scoreId={Number(scoreId)}
        isL4ScoreDetailAPIError={isL4ScoreDetailAPIError}
      />
      <ConditionalRender
        // Doctors note conditional rendering
        conditions={[
          { when: isDoctorsNoteLoading, render: <DoctorsNoteShimmer /> },
          { when: isDoctorsNoteError, render: null },
        ]}
        otherwise={renderDoctorsNotWithBg()}
      />
      <StandardContainer>
        <ConditionalRender
          // Improve my score conditional rendering
          conditions={[
            {
              when: isImproveMyScoreLoading,
              render: <ImproveMyScoreShimmer />,
            },
            { when: isImproveMyScoreError, render: null },
          ]}
          otherwise={renderImproveMyScore()}
        />
        <ConditionalRender
          // Health Connections conditional rendering
          conditions={[
            {
              when: isHealthConnectionAPILoading,
              render: (
                <section className="mb-24">
                  <SkeletonView />
                </section>
              ),
            },
            { when: isHealthConnectionAPIError, render: null },
          ]}
          otherwise={renderHealthConnections(l4ScoreDetail?.title ?? '')}
        />
      </StandardContainer>
    </div>
  );
};
